import DOMPurify from 'dompurify';

// Configure DOMPurify with safe HTML tags and attributes for rich text content
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'blockquote', 'a', 'img',
  'div', 'span'
];

const ALLOWED_ATTRIBUTES = {
  'a': ['href', 'target', 'rel'],
  'img': ['src', 'alt', 'width', 'height'],
  'table': ['class'],
  'th': ['class'],
  'td': ['class'],
  'div': ['class'],
  'span': ['class'],
  'p': ['class'],
  'strong': ['class'],
  'em': ['class'],
  'blockquote': ['class']
};

// Configure DOMPurify settings
const PURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: Object.values(ALLOWED_ATTRIBUTES).flat(),
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|blob):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Remove any script tags, event handlers, and other dangerous elements
  FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'iframe'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'style'],
  // Remove any data: URLs except for safe ones
  SANITIZE_DOM: true,
  KEEP_CONTENT: true,
  // Additional security measures
  FORCE_BODY: false,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  WHOLE_DOCUMENT: false
};

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * @param html Raw HTML content from rich text editor
 * @returns Sanitized HTML safe for rendering
 */
export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Clean the HTML with DOMPurify
  const cleanHtml = DOMPurify.sanitize(html, PURIFY_CONFIG);
  
  // Additional custom sanitization rules
  return cleanHtml
    // Remove any remaining javascript: URLs
    .replace(/javascript:/gi, '')
    // Remove any data: URLs except safe image types
    .replace(/data:(?!image\/(png|jpg|jpeg|gif|webp|svg))/gi, '')
    // Ensure external links open safely
    .replace(/<a\s+([^>]*?)href="(https?:\/\/[^"]*)"([^>]*?)>/gi, '<a $1href="$2" target="_blank" rel="noopener noreferrer"$3>')
    // Clean up any empty or malformed tags
    .replace(/<(\w+)(\s+[^>]*)?>\s*<\/\1>/g, '');
}

/**
 * Validates and sanitizes uploaded file content for security
 * @param filename Original filename
 * @returns Whether the file type is allowed
 */
export function isFileTypeAllowed(filename: string): boolean {
  const allowedExtensions = [
    '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg',
    '.pdf', '.doc', '.docx', '.txt', '.md',
    '.xls', '.xlsx', '.ppt', '.pptx'
  ];
  
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return allowedExtensions.includes(extension);
}

/**
 * Validates file size is within acceptable limits
 * @param fileSize File size in bytes
 * @returns Whether the file size is acceptable
 */
export function isFileSizeAcceptable(fileSize: number): boolean {
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  return fileSize <= MAX_FILE_SIZE;
}

/**
 * Generates a safe filename by removing potentially dangerous characters
 * @param originalName Original filename
 * @returns Sanitized filename
 */
export function sanitizeFilename(originalName: string): string {
  return originalName
    .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special chars with underscore
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .toLowerCase()
    .substring(0, 100); // Limit filename length
}
